import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ICurrency, IFacility } from "../../types/api";
import { RootState } from "../store";
import { IOfferItem } from "./../../types/Offer/index";

/** ============================= */
/**           Interfaces          */
/** ============================= */

/**
 * Represents a single cart item in an order.
 */
export interface ReturnedOrderCartItem {
  productId: number;
  offerId: number;
  packageId: number;
  quantity: number;
  price: number;
  image: string | null;
  offerItems?: IOfferItem[];
  name: string;
  nameAr: string;
  nameEn: string;
  itemType: string;
  warehouse: IFacility | undefined;
  currency: ICurrency | undefined;
}

/**
 * Represents an order which can be of type 'ALL' or 'SPECIFIC'.
 */
interface Order {
  type: "ALL" | "SPECIFIC";
  items: ReturnedOrderCartItem[];
  totalprice: number;
  warehouse: IFacility | undefined;
}

/**
 * The entire cart state, containing one or more orders and totals.
 */
interface ReturnedOrderCartState {
  orders: Order[];
  totalReturnedOrderCartPrice: number;
  totalItems: number;
  showAddOfferPopup: boolean;
}

/** ============================= */
/**       Initial State           */
/** ============================= */

const initialState: ReturnedOrderCartState = {
  orders: [],
  totalReturnedOrderCartPrice: 0,
  totalItems: 0,
  showAddOfferPopup: false,
};

/** ============================= */
/**      Utility Functions        */
/** ============================= */

/**
 * Safely convert an Immer proxy (or any value) to a plain JS object/string for logging.
 * If the value is `undefined` or `null`, returns a string instead of throwing an error.
 */
function safeToJS<T>(value: T): T | string {
  if (value === undefined) return "undefined";
  if (value === null) return "null";
  return JSON.parse(JSON.stringify(value));
}

/**
 * Finds an existing item in an order by matching productId, offerId, or packageId.
 */
function findExistingItem(
  order: Order,
  newItem: ReturnedOrderCartItem,
): ReturnedOrderCartItem | undefined {
  return order.items.find((item) => {
    if (newItem.productId) {
      return item.productId === newItem.productId;
    }
    if (newItem.offerId) {
      return item.offerId === newItem.offerId;
    }
    if (newItem.packageId) {
      return item.packageId === newItem.packageId;
    }
    return false;
  });
}

/**
 * Calculates the total price of all items in a single order.
 */
function calculateReturnedOrderTotalprice(order: Order): number {
  return order.items.reduce(
    (total, item) => total + item.quantity * item.price,
    0,
  );
}

/**
 * Calculates the total number of items across all orders in the cart.
 */
// function calculateTotalItems(orders: Order[]): number {
//   return orders.reduce(
//     (total, order) =>
//       total + order.items.reduce((sum, item) => sum + item.quantity, 0),
//     0
//   );
// }
function calculateTotalItems(orders: Order[]): number {
  return orders.reduce((total, order) => total + order.items.length, 0);
}

/** ============================= */
/**         Slice Setup           */
/** ============================= */

const returnedOrdersCart = createSlice({
  name: "returnedOrdersCart",
  initialState,
  reducers: {
    /**
     * Clears the entire cart, resetting to an empty state.
     */
    clearReturnedOrderCart: (state) => {
      state.orders = [];
      state.totalReturnedOrderCartPrice = 0;
      state.totalItems = 0;
    },

    /**
     * Adds an item to the cart. If an identical item (by productId / offerId / packageId) exists, increase its quantity.
     */
    addToReturnedOrderCart: (
      state,
      action: PayloadAction<{
        productId: number;
        offerId: number;
        packageId: number;
        quantity: number;
        price: number;
        image: string | null;
        offerItems?: unknown;
        name: string;
        nameAr: string;
        nameEn: string;
        warehouse: IFacility | undefined;
        itemType: string;
        currency: ICurrency | undefined;
        maxReturnedOrderQuantity: number;
      }>,
    ) => {
      const {
        productId,
        offerId,
        packageId,
        quantity,
        price,
        image,
        name,
        nameAr,
        nameEn,
        offerItems,
        warehouse,
        itemType,
        currency,
        maxReturnedOrderQuantity,
      } = action.payload;

      const newOrderType: "ALL" | "SPECIFIC" = warehouse ? "SPECIFIC" : "ALL";

      let existingOrder = warehouse
        ? state.orders.find((order) => order?.warehouse?.Id === warehouse.Id)
        : state.orders.find((order) => order?.type === "ALL");

      if (!existingOrder) {
        existingOrder = {
          type: newOrderType,
          items: [],
          totalprice: 0,
          warehouse,
        };
        state.orders.push(existingOrder);
      }

      const newItem: ReturnedOrderCartItem = {
        productId,
        offerId,
        packageId,
        quantity,
        price,
        image,
        offerItems: Array.isArray(offerItems) ? offerItems : [],
        name,
        nameAr,
        nameEn,
        itemType,
        warehouse,
        currency,
      };

      const existingItem = findExistingItem(existingOrder, newItem);

      if (existingItem) {
        // Check if quantity is already at max
        if (existingItem.quantity < maxReturnedOrderQuantity) {
          // Calculate new quantity but cap it at MAX_CART_QUANTITY
          const newQuantity = existingItem.quantity + quantity;
          existingItem.quantity =
            newQuantity > maxReturnedOrderQuantity
              ? maxReturnedOrderQuantity
              : newQuantity;
        }
        // else: quantity is MAX_CART_QUANTITY or more, do nothing (do not add)
      } else {
        // Only add new item if quantity is less or equal MAX_CART_QUANTITY (should be, but just in case)
        if (quantity <= maxReturnedOrderQuantity) {
          existingOrder.items.push(newItem);
        }
      }

      existingOrder.totalprice =
        calculateReturnedOrderTotalprice(existingOrder);
      state.totalReturnedOrderCartPrice = state.orders.reduce(
        (total, order) => total + order.totalprice,
        0,
      );
      state.totalItems = calculateTotalItems(state.orders);
    },

    /**
     * Removes an item from the cart, by matching productId/offerId/packageId on a target order.
     */
    removeFromReturnedOrderCart: (
      state,
      action: PayloadAction<{
        productId?: number;
        offerId?: number;
        packageId?: number;
        warehouse?: IFacility;
      }>,
    ) => {
      const { productId, offerId, packageId, warehouse } = action.payload;

      // 1. Find the order to remove from
      const targetOrder = warehouse
        ? state.orders.find((order) => order.warehouse?.Id === warehouse?.Id)
        : state.orders.find((order) => order.type === "ALL");
      // 2. If found, remove the matching item(s)
      const isMatchingItem = (item: ReturnedOrderCartItem) => {
        const matchProduct =
          productId !== undefined
            ? item.productId === productId ||
              (item.productId === null && productId === 0)
            : true;

        const matchOffer =
          offerId !== undefined
            ? item.offerId === offerId ||
              (item.offerId === null && offerId === 0)
            : true;

        const matchPackage =
          packageId !== undefined
            ? item.packageId === packageId ||
              (item.packageId === null && packageId === 0)
            : true;

        return matchProduct && matchOffer && matchPackage;
      };

      if (targetOrder) {
        targetOrder.items = targetOrder.items.filter(
          (item) => !isMatchingItem(item),
        );

        // 3. Recalculate target order total
        targetOrder.totalprice = calculateReturnedOrderTotalprice(targetOrder);

        // 4. If no items left in this order, remove the entire order
        if (targetOrder.items.length === 0) {
          state.orders = state.orders.filter((order) => order !== targetOrder);
        }

        // 5. Recalculate overall cart totals
        state.totalReturnedOrderCartPrice = state.orders.reduce(
          (total, order) => total + order.totalprice,
          0,
        );
        state.totalItems = calculateTotalItems(state.orders);
      }
    },

    /**
     * Decreases the quantity of a specific item by `quantity`; if it goes below 1, remove it entirely.
     */
    decreaseReturnedOrderQuantity: (
      state,
      action: PayloadAction<{
        productId?: number;
        offerId?: number;
        packageId?: number;
        quantity: number;
        warehouse?: IFacility;
      }>,
    ) => {
      const { productId, offerId, packageId, quantity, warehouse } =
        action.payload;

      // 1. Locate the target order
      const targetOrder = warehouse
        ? state.orders.find((order) => order.warehouse?.Id === warehouse?.Id)
        : state.orders.find((order) => order.type === "ALL");
      // 2. If found, find the item in this order

      if (targetOrder) {
        const existingItem = targetOrder.items.find(
          (item) =>
            (item.productId === productId ||
              (item.productId === null && productId === 0)) &&
            (item.offerId === offerId ||
              (item.offerId === null && offerId === 0)) &&
            (item.packageId === packageId ||
              (item.packageId === null && packageId === 0)),
        );

        // 3. Decrease quantity if the item exists
        if (existingItem) {
          existingItem.quantity -= quantity;

          // 4. Remove item entirely if <= 0
          if (existingItem.quantity <= 0) {
            targetOrder.items = targetOrder.items.filter(
              (item) =>
                item.productId !== productId ||
                item.offerId !== offerId ||
                item.packageId !== packageId,
            );
          }

          // 5. Recalculate the target order total
          targetOrder.totalprice =
            calculateReturnedOrderTotalprice(targetOrder);

          // 6. Remove the order if empty
          if (targetOrder.items.length === 0) {
            state.orders = state.orders.filter(
              (order) => order !== targetOrder,
            );
          }

          // 7. Recalculate overall cart totals
          state.totalReturnedOrderCartPrice = state.orders.reduce(
            (total, order) => total + order.totalprice,
            0,
          );
          state.totalItems = calculateTotalItems(state.orders);
        }
      }
    },
    // showAddOfferConfirmationPopup: (state, action: PayloadAction<boolean>) => {
    //   state.showAddOfferPopup = action.payload;
    // },
  },
});

/** ============================= */
/**       Selectors/Exports       */
/** ============================= */

/**
 * Selector to get the current quantity of a specific item by IDs.
 */
export const getItemQuantityById = (
  state: RootState,
  productId: number,
  offerId: number,
  packageId: number,
): number => {
  for (const order of state.cart.orders) {
    const item = order.items.find(
      (it: ReturnedOrderCartItem) =>
        it.productId === productId &&
        it.offerId === offerId &&
        it.packageId === packageId,
    );
    if (item) {
      return item.quantity;
    }
  }
  return 0;
};

export const {
  clearReturnedOrderCart,
  addToReturnedOrderCart,
  removeFromReturnedOrderCart,
  decreaseReturnedOrderQuantity,
} = returnedOrdersCart.actions;

export default returnedOrdersCart.reducer;
